import type {ReactNode} from "react";

export type TileThumb =
  | {kind: "image"; src: string | null}
  | {kind: "badge"; label: string; color: string};

type Props = {
  thumb: TileThumb;
  title: string;
  subtitle: ReactNode;
  error?: string;
  linkHref?: string;
  actions?: ReactNode;
  size?: number;
  subtitleClamp?: number;
  footer?: ReactNode;
  /**
   * Réservé au feed Documents (#1229) : `DocumentComposer` (bande de staging à l'upload,
   * hors feed) passe `false` pour ne pas hériter d'un pied vide sans utilité là-bas.
   */
  reserveFooter?: boolean;
};

const DEFAULT_SIZE_PX = 110;
const FOOTER_HEIGHT_PX = 20;

/**
 * Fixed-size tile rendering anything that can be represented as a thumbnail (image or
 * colored badge) plus a title (single line, ellipsis) and a subtitle (clamped to N lines).
 * Used by files (image preview / type badge + filename + size) and notes (NOTE badge +
 * empty title + cropped body).
 *
 * - `linkHref` makes an image thumbnail clickable (open in new tab).
 * - `actions` renders an overlay in the top-right corner.
 */
export function DocumentTile({
  thumb,
  title,
  subtitle,
  error,
  linkHref,
  actions,
  size = DEFAULT_SIZE_PX,
  subtitleClamp = 1,
  footer,
  reserveFooter = true,
}: Props) {
  const isImage = thumb.kind === "image";
  const thumbStyle = {
    width: size,
    height: size / 2,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    overflow: "hidden",
  } as const;

  const imgStyle = {width: "100%", height: "100%", objectFit: "cover"} as const;

  return (
    <div className={`position-relative ${error ? "border-danger" : ""}`} style={{width: size}}>
      <div
        className={`position-relative d-flex align-items-center justify-content-center ${
          isImage ? "bg-light" : ""
        }`}
        style={{
          ...thumbStyle,
          backgroundColor: thumb.kind === "badge" ? thumb.color : undefined,
        }}
      >
        {thumb.kind === "image" && thumb.src ? (
          linkHref ? (
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              className="d-block w-100 h-100"
            >
              <img src={thumb.src} alt={title} style={imgStyle} />
            </a>
          ) : (
            <img src={thumb.src} alt={title} style={imgStyle} />
          )
        ) : thumb.kind === "badge" ? (
          <span style={{fontWeight: 700, fontSize: Math.round(size * 0.22)}}>{thumb.label}</span>
        ) : null}
        {actions && (
          <div className="position-absolute" style={{top: 2, right: 2}}>
            {actions}
          </div>
        )}
      </div>
      <div className="px-2 py-1 small" style={{maxWidth: size}}>
        {title && (
          <div className="text-truncate" title={title}>
            {title}
          </div>
        )}
        <div
          className={`text-muted ${error ? "text-danger" : ""}`}
          style={{
            fontSize: 11,
            display: "-webkit-box",
            WebkitLineClamp: subtitleClamp,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {subtitle}
        </div>
      </div>
      {/* Pied de tuile réservé sur les tuiles du feed Documents (#1229) — vide tant qu'aucune
          provenance (attestation, futur devis/facture/compte rendu) n'a de badge à y mettre,
          pour que toutes les tuiles du feed gardent la même hauteur. */}
      {reserveFooter && <div style={{height: FOOTER_HEIGHT_PX, marginTop: 2}}>{footer}</div>}
    </div>
  );
}

export function DocumentTileFooterBadge({label}: {label: string}) {
  return (
    <div
      className="text-truncate"
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: 11,
        color: "var(--mt-color-action)",
        backgroundColor: "rgba(var(--mt-color-action-rgb), 0.14)",
        borderBottomLeftRadius: 6,
        borderBottomRightRadius: 6,
      }}
    >
      {label}
    </div>
  );
}
